import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import settingsService from "api/settingsService";
import { ENotificationType, notification } from "utils/notification";
import { getErrorMessage } from "utils/getErrorMessage";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import CloseIcon from "assets/icons/close.svg?react";
import NotaIcon from "assets/icons/nota.svg?react";
import styles from "./styles.module.scss";
import { WIZARD_STEPS } from "./stepsConfig";
import WizardAccordionItem from "./WizardAccordionItem";
import { WizardContext } from "./WizardContext";
import {
  DEFAULT_WIZARD_SETTINGS,
  EWizardStep,
  IContactFormWizardProps,
  IContactFormWizardSettings,
  WizardSettingsPatch,
  toBackendPayload,
} from "./typings";
import Form from "../../../form/src/components/Form";
import classNames from "classnames";
import workspaceService from "api/workspaceService";
import { IWorkspace, IWorkspaceMember } from "dto/IWorkspace";

// Wszystkie kroki są widoczne — filtr kroków zespołowych dla profilu prywatnego
// zniknął razem z profilami (model kluczy firmy: użytkownik zawsze ma firmę).
const visibleSteps = WIZARD_STEPS;

const ContactFormWizard = ({
  open,
  onClose,
  header,
  initialSettings,
  members: membersProp,
  onSettingsChange,
  onFinish,
  onStepChange,
  onExpandedStepChange,
  inline = false,
  startCollapsed = false,
  patchSettingsRef,
  finishRef,
}: IContactFormWizardProps) => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<IContactFormWizardSettings>(() => ({
    ...DEFAULT_WIZARD_SETTINGS,
    WorkspaceId: getWorkspaceId() ?? undefined,
    ...initialSettings,
  }));
  const [currentStep, setCurrentStep] = useState<EWizardStep>(
    EWizardStep.FormStyle,
  );
  const [expandedStep, setExpandedStep] = useState<EWizardStep | null>(
    startCollapsed ? null : EWizardStep.FormStyle,
  );
  const [stepDirty, setStepDirty] = useState(false);
  const [logoKey, setLogoKey] = useState<string | undefined>(undefined);

  // hydrate existing contact form for this workspace
  const { data: formData, refetch: refetchForms } = settingsService.useGetForm(
    settings.WorkspaceId,
  );
  const { mutate: updateContactForm, isLoading: isSaving } =
    settingsService.useUpdateContactForm();
  const { mutate: uploadLogoMutation, isLoading: isUploadingLogo } =
    settingsService.useUploadContactFormLogo();
  const { mutate: deleteLogoMutation } =
    settingsService.useDeleteContactFormLogo();
  const { mutate: fetchLogo } = settingsService.useGetContactFormLogo();

  const { data: workspaceData } = workspaceService.useGetWorkspace(
    membersProp?.length ? "" : settings.WorkspaceId ?? "",
  );
  const workspace = (workspaceData?.data as IWorkspace) ?? null;
  const members: IWorkspaceMember[] = useMemo(
    () =>
      (membersProp?.length ? membersProp : null) ?? workspace?.Members ?? [],
    [membersProp, workspace],
  );

  // ── hydrate from existing form when the wizard opens ────────────────────
  useEffect(() => {
    const forms = formData?.data ?? [];
    if (!forms.length) return;

    const f = forms[0];
    setLogoKey(f.Key);
    setSettings((prev) => ({
      ...prev,
      Theme: f.Theme ?? prev.Theme,
      RegistryOfficeUserIds: f.RegistryOfficeUserIds ?? [],
      ReceiverIds: f.ReceiverIds ?? [],
      WorkspaceMemberIds: f.WorkspaceMembersIds ?? [],
      Invitations: f.Invitations ?? [],
      BackgroundColor: f.BackgroundColor ?? prev.BackgroundColor,
      AccentColor: f.AccentColor ?? prev.AccentColor,
      FieldStyle: f.FieldStyle ?? prev.FieldStyle,
      PublishMethod: f.PublishMethod ?? prev.PublishMethod,
      HeaderTitle:
        f.HeaderTitle && f.HeaderTitle.trim().length > 0
          ? f.HeaderTitle
          : DEFAULT_WIZARD_SETTINGS.HeaderTitle,
      ShowHeader: f.ShowHeader ?? prev.ShowHeader,
    }));
  }, [formData]);

  // ── load existing logo once the key is known ────────────────────────────
  useEffect(() => {
    if (!logoKey) return;
    fetchLogo(
      { key: logoKey },
      {
        onSuccess: (resp: { data: Blob }) => {
          if (resp?.data && resp.data.size > 0) {
            setSettings((prev) => ({ ...prev, LogoBlob: resp.data }));
          }
        },
        onError: () => {
          // no existing logo — leave LogoBlob null
        },
      },
    );
  }, [logoKey, fetchLogo]);

  // ── notify parent on every settings change (live preview) ───────────────
  const onSettingsChangeRef = useRef(onSettingsChange);
  useEffect(() => {
    onSettingsChangeRef.current = onSettingsChange;
  });
  useEffect(() => {
    onSettingsChangeRef.current?.(settings);
  }, [settings]);

  // ── notify parent when active step changes ───────────────────────────────
  const onStepChangeRef = useRef(onStepChange);
  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  });
  useEffect(() => {
    onStepChangeRef.current?.(currentStep);
  }, [currentStep]);

  // ── notify parent when expanded (visible) step changes ───────────────────
  const onExpandedStepChangeRef = useRef(onExpandedStepChange);
  useEffect(() => {
    onExpandedStepChangeRef.current = onExpandedStepChange;
  });
  useEffect(() => {
    onExpandedStepChangeRef.current?.(expandedStep);
  }, [expandedStep]);

  // ── debounced auto-save to backend whenever settings mutate ─────────────
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistSettings = useCallback(
    (next: IContactFormWizardSettings) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        updateContactForm(toBackendPayload(next), {
          onError: (e: { response?: { data?: unknown } }) => {
            notification({
              title: t("contactFormWizard.saveFailed"),
              text: getErrorMessage(e?.response?.data),
              type: ENotificationType.ERROR,
            });
          },
          onSuccess: () => {
            if (!logoKey) refetchForms();
          },
        });
      }, 500);
    },
    [updateContactForm, t, logoKey, refetchForms],
  );

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  const patchSettings = useCallback(
    (patch: WizardSettingsPatch) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        persistSettings(next);
        return next;
      });
      setStepDirty(true);
    },
    [persistSettings],
  );

  const goToStep = useCallback((step: EWizardStep) => {
    if (!visibleSteps.some((s) => s.id === step)) return;
    setCurrentStep(step);
    setExpandedStep(step);
    setStepDirty(false);
  }, []);

  const toggleStep = useCallback((step: EWizardStep) => {
    setExpandedStep((prev) => (prev === step ? null : step));
    setCurrentStep((prev) => {
      const changed = prev !== step;
      if (changed) setStepDirty(false);
      return changed ? step : prev;
    });
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep((s) => {
      const idx = visibleSteps.findIndex((st) => st.id === s);
      const next =
        idx >= 0 && idx < visibleSteps.length - 1
          ? visibleSteps[idx + 1].id
          : s;
      setExpandedStep(next);
      return next;
    });
    setStepDirty(false);
  }, []);

  const goBack = useCallback(() => {
    setCurrentStep((s) => {
      const idx = visibleSteps.findIndex((st) => st.id === s);
      const prev = idx > 0 ? visibleSteps[idx - 1].id : s;
      setExpandedStep(prev);
      return prev;
    });
    setStepDirty(false);
  }, []);

  const uploadLogo = useCallback(
    (file: File) => {
      if (!logoKey) {
        notification({
          title: t("contactFormWizard.logoNotReady"),
          text: t("contactFormWizard.logoNotReadyHint"),
          type: ENotificationType.ERROR,
        });
        return;
      }
      uploadLogoMutation(
        { file, key: logoKey },
        {
          onSuccess: () => {
            // mirror locally so the preview shows the new logo immediately
            setSettings((prev) => ({ ...prev, LogoBlob: file }));
          },
          onError: (e: { response?: { data?: unknown } }) => {
            notification({
              title: t("contactFormWizard.logoUploadFailed"),
              text: getErrorMessage(e?.response?.data),
              type: ENotificationType.ERROR,
            });
          },
        },
      );
    },
    [logoKey, uploadLogoMutation, t],
  );

  const deleteLogo = useCallback(() => {
    if (!logoKey) {
      setSettings((prev) => ({ ...prev, LogoBlob: null }));
      return;
    }
    deleteLogoMutation(
      { key: logoKey },
      {
        onSuccess: () => {
          setSettings((prev) => ({ ...prev, LogoBlob: null }));
        },
        onError: (e: { response?: { data?: unknown } }) => {
          notification({
            title: t("contactFormWizard.logoDeleteFailed"),
            text: getErrorMessage(e?.response?.data),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  }, [logoKey, deleteLogoMutation, t]);

  // Expose internal functions to parent via refs (used in ESzyfrowaneTab inline mode).
  useEffect(() => {
    if (patchSettingsRef) patchSettingsRef.current = patchSettings;
  }, [patchSettings, patchSettingsRef]);

  const handleFinish = useCallback(() => {
    updateContactForm(toBackendPayload(settings), {
      onSuccess: () => {
        onFinish?.(settings);
        onClose?.();
      },
      onError: (e: { response?: { data?: unknown } }) => {
        notification({
          title: t("contactFormWizard.saveFailed"),
          text: getErrorMessage(e?.response?.data),
          type: ENotificationType.ERROR,
        });
      },
    });
  }, [updateContactForm, settings, onFinish, onClose, t]);

  useEffect(() => {
    if (finishRef) finishRef.current = handleFinish;
  }, [handleFinish, finishRef]);

  const currentStepIndex = visibleSteps.findIndex((s) => s.id === currentStep);
  const isFirstStep = currentStepIndex <= 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  const contextValue = useMemo(
    () => ({
      settings,
      patchSettings,
      members,
      currentStep,
      expandedStep,
      goToStep,
      toggleStep,
      goNext,
      goBack,
      isFirstStep,
      isLastStep,
      isSaving,
      isUploadingLogo,
      stepDirty,
      inline,
      logoKey,
      uploadLogo,
      deleteLogo,
      finish: handleFinish,
    }),
    [
      settings,
      patchSettings,
      members,
      currentStep,
      expandedStep,
      goToStep,
      toggleStep,
      goNext,
      goBack,
      isFirstStep,
      isLastStep,
      isSaving,
      isUploadingLogo,
      stepDirty,
      inline,
      logoKey,
      uploadLogo,
      deleteLogo,
      handleFinish,
    ],
  );

  const getThemeStyle = () => {
    switch (settings?.Theme) {
      case 0:
        return styles.theme0;
      case 1:
        return styles.theme1;
      case 2:
        return styles.theme2;
      case 3:
        return styles.theme3;
      case 4:
        return styles.theme4;
      case 5:
        return styles.theme5;
      case 6:
        return styles.theme6;
      default:
        styles.theme0;
    }
  };

  if (inline) {
    return (
      <WizardContext.Provider value={contextValue}>
        <aside
          className={styles.sidebar}
          style={{ flex: 1, maxWidth: "100%", height: "100%" }}
        >
          <div className={styles.sidebarInner}>
            {WIZARD_STEPS.map((step, i) => (
              <div key={step.id}>
                {step.stageLabel && (
                  <div className={styles.stageLabel}>{step.stageLabel}</div>
                )}
                <WizardAccordionItem step={step} index={i} />
              </div>
            ))}
          </div>
        </aside>
      </WizardContext.Provider>
    );
  }

  if (!open) return null;

  return ReactDOM.createPortal(
    <WizardContext.Provider value={contextValue}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div
          className={classNames(styles.header, header && styles.headerCustom)}
        >
          {header ?? (
            <div className={styles.headline}>
              {t("contactFormWizard.title")}
            </div>
          )}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose ?? undefined}
            aria-label={t("common.close")}
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.body}>
          <div className={classNames(styles.canvasBackground, getThemeStyle())}>
            <div className={styles.bgl}></div>
            <div className={styles.bgr}></div>
          </div>
          <div className={styles.canvas}>
            <div className={styles.logo}>
              {settings?.LogoBlob && (
                <img src={URL.createObjectURL(settings.LogoBlob)} alt="Logo" />
              )}
            </div>

            <div className={styles.canvasContent}>
              <div className={styles.centerSlot}>
                <div className={styles.formWrapper}>
                  <Form
                    inline={true}
                    id={"test"}
                    settings={{
                      Theme: contextValue.settings.Theme,
                      FieldStyle: contextValue.settings.FieldStyle,
                      AccentColor: contextValue.settings.AccentColor,
                      HeaderTitle: contextValue.settings.HeaderTitle,
                      ShowHeader: contextValue.settings.ShowHeader,
                      Receivers:
                        contextValue.settings.ReceiverIds && members
                          ? [
                              settings.RegistryOfficeUserIds &&
                              settings.RegistryOfficeUserIds.length > 0
                                ? {
                                    Id: "regOffice",
                                    Label: "Sekretariat",
                                    UserIds: settings.RegistryOfficeUserIds,
                                  }
                                : undefined,
                              ...members
                                .filter(
                                  (x) =>
                                    contextValue.settings.ReceiverIds.indexOf(
                                      x.UserId,
                                    ) !== -1,
                                )
                                .map((x) => ({
                                  Id: x.Id,
                                  Label: x.Label,
                                  UserIds: [],
                                })),
                            ].filter((x) => !!x)
                          : [],
                    }}
                    demo={true}
                  ></Form>
                </div>
              </div>
              {/* <div className={styles.centerSlotEmpty}>
                  <div className={styles.stack}>
                    <EyeIcon className={styles.eye} />
                    <div>{t("contactFormWizard.livePreviewSlot")}</div>
                  </div>
                </div> */}
            </div>

            <div className={styles.poweredBy}>
              <span>{t("contactFormWizard.poweredBy")}</span>
              <NotaIcon />
            </div>
          </div>

          <aside className={styles.sidebar}>
            <div className={styles.sidebarInner}>
              {WIZARD_STEPS.map((step, i) => (
                <div key={step.id}>
                  {step.stageLabel && (
                    <div className={styles.stageLabel}>{step.stageLabel}</div>
                  )}
                  <WizardAccordionItem step={step} index={i} />
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </WizardContext.Provider>,
    document.body,
  );
};

export default ContactFormWizard;
